import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const authApiBaseUrl = process.env.REACT_APP_AUTH_API_BASE_URL;

const getApiMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const ForgotPassword = () => {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);

  const normalizedEmail = useMemo(() => email.trim().toLowerCase(), [email]);

  useEffect(() => {
    const tokenFromUrl = new URLSearchParams(window.location.search).get("token");
    if (tokenFromUrl) {
      setResetToken(tokenFromUrl);
      setStep("password");
      setAlert({ type: "success", msg: t("resetPassword.enterNewPassword") });
    }
  }, [t]);

  const sendOtp = async (event) => {
    event.preventDefault();
    setAlert(null);

    if (!normalizedEmail) {
      setAlert({ type: "error", msg: t("resetPassword.emailRequired") });
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${authApiBaseUrl}/forgot-password`, {
        email: normalizedEmail,
      });
      setStep("code");
      setAlert({ type: "success", msg: t("resetPassword.codeSent") });
    } catch (error) {
      setAlert({
        type: "error",
        msg: getApiMessage(error, t("resetPassword.codeSendFailed")),
      });
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (event) => {
    event.preventDefault();
    setAlert(null);

    if (!normalizedEmail || !code.trim()) {
      setAlert({ type: "error", msg: t("resetPassword.emailAndCodeRequired") });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${authApiBaseUrl}/verify-reset-code`, {
        email: normalizedEmail,
        code: code.trim(),
      });
      setResetToken(response?.data?.resetToken || "");
      setStep("password");
      setAlert({ type: "success", msg: t("resetPassword.codeVerified") });
    } catch (error) {
      setAlert({
        type: "error",
        msg: getApiMessage(error, t("resetPassword.invalidCode")),
      });
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (event) => {
    event.preventDefault();
    setAlert(null);

    if (!resetToken || !newPassword || !confirmPassword) {
      setAlert({ type: "error", msg: t("resetPassword.passwordRequired") });
      return;
    }

    if (newPassword !== confirmPassword) {
      setAlert({ type: "error", msg: t("resetPassword.passwordMismatch") });
      return;
    }

    if (newPassword.trim().length < 6) {
      setAlert({ type: "error", msg: t("resetPassword.passwordTooShort") });
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${authApiBaseUrl}/reset-password`, {
        resetToken,
        newPassword,
      });
      setStep("done");
      setAlert({ type: "success", msg: t("resetPassword.passwordReset") });
    } catch (error) {
      setAlert({
        type: "error",
        msg: getApiMessage(error, t("resetPassword.passwordResetFailed")),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="flex login items-center font-cairo justify-center min-h-screen bg-gray-100 px-4">
      <div className="w-full max-w-md bg-white shadow-lg rounded-lg p-6" dir={i18n.language === "ar" ? "rtl" : "ltr"}>
        <div className="text-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-800">{t("resetPassword.title")}</h1>
          <p className="text-sm text-gray-500 mt-2">
            {resetToken ? t("resetPassword.passwordSubtitle") : t("resetPassword.emailSubtitle")}
          </p>
        </div>

        {alert?.msg && (
          <p
            className={`mb-4 rounded-md px-3 py-2 text-sm font-semibold ${
              alert.type === "error"
                ? "bg-red-50 text-red-700 border border-red-100"
                : "bg-green-50 text-green-700 border border-green-100"
            }`}
          >
            {alert.msg}
          </p>
        )}

        {step === "email" && (
          <form onSubmit={sendOtp} className="flex flex-col gap-4">
            <div>
              <label className="block text-sm text-[#251658] font-bold mb-2">{t("email")}</label>
              <input
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full text-center font-bold px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#251c54]"
              />
            </div>
            <button
              type="submit"
              className="w-full bg-[#251658] text-white py-2 rounded-lg hover:bg-[#1d1545] transition cursor-pointer disabled:opacity-70"
              disabled={loading}
            >
              {loading ? t("resetPassword.sending") : t("resetPassword.sendOtp")}
            </button>
          </form>
        )}

        {step === "code" && (
          <form onSubmit={verifyCode} className="flex flex-col gap-4">
            <div>
              <label className="block text-sm text-[#251658] font-bold mb-2">{t("resetPassword.otp")}</label>
              <input
                type="text"
                required
                value={code}
                onChange={(event) => setCode(event.target.value)}
                className="w-full text-center font-bold px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#251c54]"
              />
            </div>
            <button
              type="submit"
              className="w-full bg-[#251658] text-white py-2 rounded-lg hover:bg-[#1d1545] transition cursor-pointer disabled:opacity-70"
              disabled={loading}
            >
              {loading ? t("resetPassword.verifying") : t("resetPassword.verifyOtp")}
            </button>
          </form>
        )}

        {step === "password" && (
          <form onSubmit={resetPassword} className="flex flex-col gap-4">
            <div>
              <label className="block text-sm text-[#251658] font-bold mb-2">{t("resetPassword.newPassword")}</label>
              <input
                type="password"
                required
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                className="w-full text-center font-bold px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#251c54]"
              />
            </div>
            <div>
              <label className="block text-sm text-[#251658] font-bold mb-2">{t("resetPassword.confirmPassword")}</label>
              <input
                type="password"
                required
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="w-full text-center font-bold px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#251c54]"
              />
            </div>
            <button
              type="submit"
              className="w-full bg-[#251658] text-white py-2 rounded-lg hover:bg-[#1d1545] transition cursor-pointer disabled:opacity-70"
              disabled={loading}
            >
              {loading ? t("resetPassword.saving") : t("resetPassword.reset")}
            </button>
          </form>
        )}

        {step === "done" && (
          <Link
            to="/login"
            className="block w-full text-center bg-[#251658] text-white py-2 rounded-lg hover:bg-[#1d1545] transition"
          >
            {t("resetPassword.backToLogin")}
          </Link>
        )}

        {step !== "done" && (
          <Link to="/login" className="block text-center text-sm text-[#251658] hover:underline mt-5">
            {t("resetPassword.backToLogin")}
          </Link>
        )}
      </div>
    </section>
  );
};

export default ForgotPassword;
